import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Loader from 'components/Kizen/Loader';
import SurveyService from 'services/SurveyService';
import { setSearchParams, getSearchParam } from 'hooks/useSearchParam';
import { isMobile, useWindowSize } from 'app/spacing';
import ConfirmDeletionModal from 'components/Modals/presets/ConfirmDeletion';
import { getOrderingParam, getSortValue } from 'utility/SortingHelpers';
import { setOpenMenuAbove } from 'components/Tables/helpers';
import useModal from 'components/Modals/useModal';
import {
  buildPage,
  getSurveys,
  updatePageConfig,
  updateSurveyFieldSuccess,
  duplicateSurvey,
  deleteSurvey,
  cleanDeleteSurveyToastMessage,
  selectAddSurveyModels,
  selectSurveys,
  selectSurveysCount,
  selectSurveysPageConfig,
  selectSurveysDeleteToastMessage,
  addNewSurvey,
  surveysIsFetching,
  updateSurveyAndEdit,
  updateNewlyGeneratedSurveyId,
} from 'store/surveysPage/surveys.redux';
import SurveyTabularPage from './TabularPage';
import SurveyListPage from './ListPage';
import AddModalComponent from './AddModal';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { useToast, toastVariant } from 'components/ToastProvider';
import { useDebounce } from 'react-use';
import { maybeTrackBuilderPreview, maybeTrackSearch } from 'utility/analytics';
import { DEFAULT_DELAY } from 'utility/config';
import { deferExecution } from 'utility/defer';
import { useSyncDispatch } from 'ts-components/hooks/useSyncDispatch';
import { getCreateEntityAccess } from 'store/authentication/selectors';
import { useOpenAiChatTrigger } from 'ts-components/AiChatModal/useOpenAiChatModalTrigger';
import { useAIFeatures, AI_FEATURE_CONFIG_KEYS } from 'hooks/useAIFeatures';
import { GUARD_RAIL_CODES, FLOW_STATUS } from 'ts-components/Conversation';
import { monitoringExceptionHelper } from 'instrumentation/helpers';

export const previewSurvey = (...args) => {
  const preview =
    (businessUrl) =>
    (slug, windowName = '_blank') => {
      maybeTrackBuilderPreview('survey', slug);
      const win = window.open(`${businessUrl}/survey/${slug}`, windowName);
      win.focus();
    };

  const [url, slug, windowName] = args;
  return slug ? preview(url)(slug, windowName) : preview(url);
};

export default function SurveysPage(props) {
  const { t } = useTranslation();
  useSetTitleOnLoad(props.title(t));
  const history = useHistory();
  const dispatch = useDispatch();
  const syncDispatch = useSyncDispatch();
  const { width } = useWindowSize();

  const [showToast] = useToast();

  // TODO: when hosting standalone forms we'll want to use the configurable subdomain from Settings -> Business Information
  const chosenBusinessUrl = `https://${import.meta.env.VITE_EMBED_FRONTEND_DOMAIN}`;
  // const chosenBusinessUrl = useSelector(
  //   ({ authentication }) =>
  //     !!authentication.chosenBusiness && authentication.chosenBusiness.url
  // );

  const models = useSelector(selectAddSurveyModels);
  const pageConfig = useSelector(selectSurveysPageConfig);
  const surveysCount = useSelector(selectSurveysCount);
  const surveysWithoutMeta = useSelector(selectSurveys);
  const isFetching = useSelector(surveysIsFetching);
  const createEntityAccess = useSelector(getCreateEntityAccess);
  const newlyGeneratedSurveyId = useSelector(
    (state) => state.surveysPage.newlyGeneratedSurveyId
  );
  const { aiFeaturesEnabled } = useAIFeatures(AI_FEATURE_CONFIG_KEYS.survey_builder);

  // Track debounced changes to the search term and send to google analytics
  const { search } = pageConfig;
  useDebounce(() => maybeTrackSearch(search, 'surveys'), DEFAULT_DELAY, [
    search,
  ]);

  // async effects -------------------------------------------------
  useEffect(() => {
    const sort = getSearchParam(history.location, 'sort');
    const search = getSearchParam(history.location, 'q');
    const page = getSearchParam(history.location, 'page');
    const size = getSearchParam(history.location, 'size');

    syncDispatch(
      buildPage({
        page: {
          search,
          sort,
          page: page && parseInt(page, 10),
          size: size && parseInt(size, 10),
        },
      })
    );
  }, [syncDispatch, history]);

  const surveys = useMemo(() => {
    return surveysWithoutMeta && surveysWithoutMeta.map(setOpenMenuAbove);
  }, [surveysWithoutMeta]);

  const extractGenerationResult = useCallback((response) => {
    const runMetadata = response.run_metadata;
    const guardrailCode = runMetadata?.form_surveys?.guardrail_code;
    const guardrailMessage = runMetadata?.form_surveys?.guardrail_message;
    const sessionId = response.session_id || runMetadata?.session_id;
    const generatedSurvey =
      Object.keys(runMetadata?.form_surveys || {}).length > 0
        ? Object.values(runMetadata?.form_surveys)?.[0]
        : null;

    return {
      hasResult: !!generatedSurvey,
      sessionId,
      guardrailCode,
      guardrailMessage,
    };
  }, []);

  const onGenerationSuccess = useCallback(
    (surveyFlowStatusData) => {
      const { run_metadata = {}, status } = surveyFlowStatusData;

      // Find the ready survey (has id and status: "ready")
      const allSurveys = Object.values(run_metadata?.form_surveys || {});
      const readySurvey = allSurveys.find(
        (survey) => survey.id && survey.status === 'ready'
      );

      // Handle flow with next_actions (chat session or one-shot with URL)
      const navigateUrl =
        surveyFlowStatusData?.next_actions?.[0]?.url || readySurvey?.url;
      if (navigateUrl) {
        try {
          const guardrailCode = run_metadata?.form_surveys?.guardrail_code;
          const guardrailMessage =
            run_metadata?.form_surveys?.guardrail_message;
          const url = new URL(navigateUrl);
          const relativePath = url.pathname + url.search;
          if (guardrailCode && guardrailMessage) {
            showToast({
              message: guardrailMessage,
              variant:
                guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
                  ? toastVariant.ALERT
                  : toastVariant.FAILURE,
              autohide: false,
            });
          } else {
            showToast({
              message: t('Survey Generated Successfully'),
              variant: toastVariant.SUCCESS,
            });
          }
          setTimeout(() => {
            history.push(relativePath);
          }, 1500);
          return;
        } catch (error) {
          monitoringExceptionHelper(error, {
            extra: {
              nextActionUrl: navigateUrl,
            },
          });
        }
      }

      // Handle guardrail messages
      const guardrailCode = run_metadata?.form_surveys?.guardrail_code;
      const guardrailMessage = run_metadata?.form_surveys?.guardrail_message;

      if (guardrailCode && guardrailMessage) {
        showToast({
          message: guardrailMessage,
          variant:
            guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
              ? toastVariant.ALERT
              : toastVariant.FAILURE,
          autohide: false,
        });
        if (readySurvey?.id) {
          deferExecution(() => {
            dispatch(updateNewlyGeneratedSurveyId(readySurvey.id));
          });
        }
      } else if (
        readySurvey?.id &&
        status !== FLOW_STATUS.FAILED &&
        readySurvey?.status !== FLOW_STATUS.FAILED
      ) {
        showToast({
          message: t('Survey Generated Successfully'),
          variant: toastVariant.SUCCESS,
        });
        setTimeout(() => {
          dispatch(updateNewlyGeneratedSurveyId(readySurvey.id));
        }, 1500);
      } else {
        showToast({
          message: t('Survey Generation Failed, Please try again later'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [showToast, dispatch, history, t]
  );

  const onGenerationFailure = useCallback(
    (error) => {
      const usageError = error?.response?.data?.run_context?.usage;
      if (usageError && usageError[0]) {
        showToast({
          variant: toastVariant.FAILURE,
          message: usageError[0],
        });
      } else {
        showToast({
          variant: toastVariant.FAILURE,
          message: t('Survey Generation Failed, Please try again later'),
        });
      }
    },
    [showToast, t]
  );

  const handleChangeSearch = useCallback(
    (q) => {
      dispatch(
        updatePageConfig({
          search: q,
          page: 1,
        })
      );
      dispatch(getSurveys());
      deferExecution(() => {
        setSearchParams(history, { q, page: null }, { method: 'replace' });
      });
    },
    [dispatch, history]
  );

  const sortValue = useMemo(
    () => getSortValue(pageConfig.sort),
    [pageConfig.sort]
  );

  const handleSort = useCallback(
    ({ column, direction }) => {
      const value = getOrderingParam({ column, direction });

      dispatch(
        updatePageConfig({
          sort: value,
        })
      );
      dispatch(getSurveys());
      deferExecution(() => {
        setSearchParams(
          history,
          {
            sort: value,
          },
          { method: 'replace' }
        );
      });
    },
    [dispatch, history]
  );

  const handleChangePageNumber = useCallback(
    (value) => {
      dispatch(
        updatePageConfig({
          page: value,
        })
      );
      dispatch(getSurveys());
      deferExecution(() => {
        setSearchParams(history, {
          page: value,
        });
      });
    },
    [dispatch, history]
  );

  const handleChangePageSize = useCallback(
    (value) => {
      dispatch(
        updatePageConfig({
          size: value,
        })
      );
      dispatch(getSurveys());
      deferExecution(() => {
        setSearchParams(history, {
          size: value,
        });
      });
    },
    [dispatch, history]
  );

  const handleUpdateSurveyField = useCallback(
    async (survey, patch) => {
      try {
        const updatedSurvey = await SurveyService.update(survey.id, patch);
        dispatch(updateSurveyFieldSuccess(updatedSurvey));
      } catch (err) {
        let error = err;
        if (err.response && err.response.data && err.response.data.errors) {
          const errors = Object.entries(err.response.data.errors);
          if (errors && errors.length) {
            // we show only 1st error
            error = new Error(errors[0][1]);
          }
        }
        throw error;
      }
    },
    [dispatch]
  );

  const handleEditSurvey = useCallback(
    (id) => {
      dispatch(updateNewlyGeneratedSurveyId(''));
      history.push(`/surveys/${id}`);
    },
    [history, dispatch]
  );

  const handleDuplicateSurvey = (id) => dispatch(duplicateSurvey(id));

  const handleConfirmDeleteSurvey = (id) => dispatch(deleteSurvey(id));

  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  const handleDeleteSurvey = (id) => setDeleteConfirmation({ id });

  const surveyDeleteToastMessage = useSelector(selectSurveysDeleteToastMessage);
  useEffect(() => {
    if (surveyDeleteToastMessage) {
      showToast({
        variant: surveyDeleteToastMessage.variant,
        message: surveyDeleteToastMessage.message,
      });
      dispatch(cleanDeleteSurveyToastMessage());
    }
  }, [dispatch, surveyDeleteToastMessage, showToast]);

  const handleCreate = useCallback(
    async (newSurvey) => {
      if (newSurvey?.ai && newSurvey?.error) {
        showToast({
          message: newSurvey.error,
          variant: toastVariant.FAILURE,
        });
      } else {
        if (newSurvey?.ai) {
          showToast({
            message: t('Survey created successfully in Kizen AI Lab'),
            variant: toastVariant.SUCCESS,
          });
          dispatch(updateSurveyAndEdit(newSurvey));
        }
        dispatch(addNewSurvey(newSurvey));
        handleEditSurvey(newSurvey.id);
      }
    },
    [dispatch, handleEditSurvey, showToast, t]
  );

  useEffect(() => {
    if (newlyGeneratedSurveyId) {
      handleEditSurvey(newlyGeneratedSurveyId);
    }
  }, [newlyGeneratedSurveyId, handleEditSurvey]);

  const [addModalProps, , addModal] = useModal({
    handleSubmit: handleCreate,
  });

  useOpenAiChatTrigger(addModal.show, addModalProps.show);

  if (isFetching) {
    return <Loader loading />;
  }

  return (
    <>
      {deleteConfirmation && (
        <ConfirmDeletionModal
          show
          onHide={() => setDeleteConfirmation(null)}
          onConfirm={async () => {
            const { id } = deleteConfirmation;
            try {
              await handleConfirmDeleteSurvey(id);
            } finally {
              setDeleteConfirmation(null);
            }
          }}
        >
          {t('This will permanently delete the survey.')}
        </ConfirmDeletionModal>
      )}
      {isMobile(width) ? (
        <SurveyListPage
          surveys={surveys || []}
          surveysCount={surveysCount}
          search={pageConfig.search}
          onChangeSearch={handleChangeSearch}
          sort={sortValue}
          onChangeSort={handleSort}
          pageNumber={pageConfig.page || 1}
          pageSize={pageConfig.size || 50}
          onChangePageSize={handleChangePageSize}
          onChangePageNumber={handleChangePageNumber}
          onSelectPreviewSurvey={previewSurvey(chosenBusinessUrl)}
          onSelectDeleteSurvey={handleDeleteSurvey}
          createEntityAccess={createEntityAccess}
          {...props}
        />
      ) : (
        <SurveyTabularPage
          surveys={surveys || []}
          surveysCount={surveysCount}
          search={pageConfig.search}
          onChangeSearch={handleChangeSearch}
          sort={sortValue}
          onChangeSort={handleSort}
          pageNumber={pageConfig.page || 1}
          pageSize={pageConfig.size || 50}
          onChangePageSize={handleChangePageSize}
          onChangePageNumber={handleChangePageNumber}
          onUpdateSurveyField={handleUpdateSurveyField}
          onSelectEditSurvey={handleEditSurvey}
          onSelectPreviewSurvey={previewSurvey(chosenBusinessUrl)}
          onSelectDuplicateSurvey={handleDuplicateSurvey}
          onSelectDeleteSurvey={handleDeleteSurvey}
          openAddModal={addModal.show}
          createEntityAccess={createEntityAccess}
          aiFeaturesEnabled={aiFeaturesEnabled}
          onGenerationSuccess={onGenerationSuccess}
          onGenerationFailure={onGenerationFailure}
          extractGenerationResult={extractGenerationResult}
          {...props}
        />
      )}
      {addModal.show && (
        <AddModalComponent models={models} {...addModalProps} />
      )}
    </>
  );
}
